import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

const usd = (c) => `$${(c / 100).toFixed(2)}`;

function AttributionTable({ api }) {
  const [touch, setTouch] = useState("last");
  const [days, setDays] = useState(90);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/admin/attribution?touch=${touch}&days=${days}`).then((r) => setD(r.data)).catch(() => setD({ rows: [], totals: {}, error: true })); }, [touch, days]); // eslint-disable-line
  return (
    <div data-testid="attribution-report">
      <div className="flex flex-wrap items-center gap-2">
        {["last", "first"].map((t) => (
          <button key={t} onClick={() => setTouch(t)} data-testid={`attr-touch-${t}`}
            className={`px-3 py-1.5 border-2 font-head uppercase text-xs ${touch === t ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E]"}`}>{t}-touch</button>
        ))}
        <select value={days} onChange={(e) => setDays(+e.target.value)} data-testid="attr-days" className="border-2 border-[#D3CFC0] bg-[#E7E3D9] px-2 py-1.5 text-sm">
          {[7, 30, 90, 365].map((n) => <option key={n} value={n}>{`Last ${n} days`}</option>)}
        </select>
        <span className="text-xs text-[#6E6E6E]">Test, internal and Stripe test-mode records are excluded.</span>
      </div>
      {!d ? <div className="mt-6 flex items-center gap-2 text-[#6E6E6E]"><Loader2 className="animate-spin" size={16} /> Loading…</div> : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm border-2 border-[#D3CFC0]">
            <thead className="bg-[#2D2D2D] text-[#F3F1EC] font-head uppercase text-xs">
              <tr>{["Source", "Medium", "Campaign", "Signups", "Checkouts", "Orders", "Revenue"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr>
            </thead>
            <tbody>
              {d.rows.map((r, i) => (
                <tr key={i} className="border-t border-[#D3CFC0]" data-testid={`attr-row-${i}`}>
                  <td className="px-3 py-2">{r.source}</td><td className="px-3 py-2">{r.medium}</td><td className="px-3 py-2">{r.campaign}</td>
                  <td className="px-3 py-2">{r.signups}</td><td className="px-3 py-2">{r.checkouts}</td>
                  <td className="px-3 py-2">{r.orders}{Object.keys(r.by_kind).length > 0 && <span className="text-xs text-[#6E6E6E]"> ({Object.entries(r.by_kind).map(([k, v]) => `${k} ${v}`).join(", ")})</span>}</td>
                  <td className="px-3 py-2">{usd(r.revenue_cents)}</td>
                </tr>
              ))}
              {d.rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-[#6E6E6E]" data-testid="attr-empty">No signups or orders in this window yet.</td></tr>}
            </tbody>
            {d.rows.length > 0 && (
              <tfoot className="border-t-2 border-[#2D2D2D] font-semibold" data-testid="attr-totals">
                <tr><td className="px-3 py-2" colSpan={3}>Total</td><td className="px-3 py-2">{d.totals.signups}</td><td className="px-3 py-2">{d.totals.checkouts}</td><td className="px-3 py-2">{d.totals.orders}</td><td className="px-3 py-2">{usd(d.totals.revenue_cents)}</td></tr>
              </tfoot>
            )}
          </table>
          {d.excluded && (
            <p className="mt-2 text-xs text-[#6E6E6E]" data-testid="attr-excluded">
              Excluded from these numbers (still in Stripe): {d.excluded.checkouts} checkouts, {d.excluded.orders} paid orders, {usd(d.excluded.revenue_cents)}
              {Object.keys(d.excluded.by_reason).length > 0 && ` · ${Object.entries(d.excluded.by_reason).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(", ")}`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function KitFollowup({ api }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/admin/kit-followup/preview").then((r) => setD(r.data)).catch(() => setD({ steps: [] })); }, []); // eslint-disable-line
  const activate = async () => {
    try { const r = await api.post("/admin/kit-followup/activate"); setMsg(`Activated ${r.data.activated} held journeys.`); }
    catch (e) { setMsg(e.response?.data?.detail || "Couldn't activate."); }
  };
  if (!d) return null;
  return (
    <div className="mt-12" data-testid="kit-followup-admin">
      <h3 className="font-head uppercase text-xl text-[#1E1E1E]">Starter Kit buyer follow-up</h3>
      <p className="mt-1 text-sm text-[#6E6E6E]" data-testid="kit-followup-status">
        Status: <b>{d.enabled ? "ENABLED" : "OFF (awaiting copy approval)"}</b> · {d.held} opt-in buyer{d.held === 1 ? "" : "s"} waiting.
        Opt-in buyers only; sample shown for Basketball 7-9.
      </p>
      <div className="mt-4 space-y-4">
        {d.steps.map((s) => (
          <details key={s.day} className="border-2 border-[#D3CFC0] bg-[#F3F1EC]" data-testid={`kit-followup-step-${s.day}`}>
            <summary className="cursor-pointer px-4 py-3 font-head uppercase text-sm">Day {s.day}: {s.subject}</summary>
            <div className="px-4 pb-4 text-[#1E1E1E]" dangerouslySetInnerHTML={{ __html: s.body }} />
          </details>
        ))}
      </div>
      <button onClick={activate} disabled={!d.enabled} data-testid="kit-followup-activate"
        className="mt-4 px-4 py-2 border-2 border-[#2D2D2D] font-head uppercase text-xs disabled:opacity-40">Activate held journeys</button>
      {msg && <p className="mt-2 text-sm" data-testid="kit-followup-msg">{msg}</p>}
    </div>
  );
}

export default function FunnelAdmin({ api }) {
  return (
    <div className="mt-8" data-testid="funnel-admin">
      <h2 className="font-head uppercase text-2xl text-[#1E1E1E] mb-4">Signups &amp; orders by source</h2>
      <AttributionTable api={api} />
      <KitFollowup api={api} />
    </div>
  );
}
